import { Fieldset, Legend } from '@headlessui/react'
import { DetailRow } from '../components/DetailRow'
import { DetailRowWithPills } from '../components/DetailRowWithPills'
import ReadMoreText from '../components/ReadMoreText'
import { useEffect, useState } from 'react'
import { StarRatingInput } from '../components/StarRatingInput'
import { useParams, useSearchParams } from 'react-router-dom'
import { getApprovedSubmissionBySlug } from '../pocketbase/PocketbaseApprovedSubmissionsService'
import { useFragranceAverageRating } from '../queries/useReviews'
import { pb } from '../pocketbase/Pocketbase'
import { NavBar } from '../components/NavBar'
import { ReviewsSection } from '../components/ReviewsSection'
import { SingleReviewSection } from '../components/SingleReviewSection'
import type { IApprovedSubmissionWithMetadata } from './dashboard/approved/ApprovedSubmissionsTable'
import { WriteReviewDialog } from '../components/dialogs/WriteReviewDialog'

export const DetailsPage = () => {
  const { slug } = useParams<{ slug: string }>()
  const [searchParams] = useSearchParams()
  const reviewId = searchParams.get('reviewId')
  const replyId = searchParams.get('replyId')
  const [fragrance, setFragrance] =
    useState<IApprovedSubmissionWithMetadata | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false)
  const [isReviewDialogOpen, setIsReviewDialogOpen] = useState(false)
  const [initialReviewRating, setInitialReviewRating] = useState(0)

  // Use react-query for average rating (will update when reviews change)
  const { data: ratingData } = useFragranceAverageRating(fragrance?.id ?? '')
  const averageRating = ratingData?.average ?? 0
  const reviewCount = ratingData?.count ?? 0

  useEffect(() => {
    window.scrollTo(0, 0)
    loadFragrance()
  }, [])

  const loadFragrance = async () => {
    if (!slug) {
      console.error('No fragrance slug provided')
      setIsLoading(false)
      return
    }

    try {
      setIsLoading(true)
      const submission = await getApprovedSubmissionBySlug(slug)

      // Get image URL
      let imgUrl: string | null = null
      if (submission.image) {
        imgUrl = pb.files.getURL(submission, submission.image)
      }

      // Use expanded relations directly from PocketBase response
      const fragranceWithMetadata: IApprovedSubmissionWithMetadata = {
        id: submission.id,
        name: submission.name,
        slug: submission.slug,
        imgUrl,
        description: submission.description || '',
        year: submission.year || null,
        brand: submission.expand?.brand,
        country: submission.expand?.country,
        perfumers: submission.expand?.perfumers || [],
        price: submission.expand?.price,
        topNotes: submission.expand?.top_notes || [],
        middleNotes: submission.expand?.middle_notes || [],
        bottomNotes: submission.expand?.bottom_notes || [],
        submitted_by: submission.submitted_by,
        created: submission.created,
      }

      setFragrance(fragranceWithMetadata)
    } catch (error) {
      console.error('Error loading fragrance:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleRatingClicked = (rating: number) => {
    setInitialReviewRating(rating)
    setIsReviewDialogOpen(true)
  }

  if (isLoading) {
    return (
      <div
        className={
          'min-h-screen flex items-center justify-center bg-background'
        }
      >
        <div className={'md:text-2xl text-xl'}>Loading...</div>
      </div>
    )
  }

  if (!fragrance) {
    return (
      <div className={'min-h-screen flex flex-col bg-background'}>
        <NavBar />
        <div className={'flex-1 flex items-center justify-center'}>
          <div className={'md:text-2xl text-xl'}>Fragrance not found</div>
        </div>
      </div>
    )
  }

  const priceLabel = fragrance.price?.name || 'N/A'



  return (
    <>
      <div className={'min-h-screen flex flex-col items-center bg-background'}>
        <NavBar />
        <div className={'w-full bg-white flex justify-center'}>
          <div
            className={
              'flex flex-col items-center md:gap-10 gap-6 md:mx-6 mx-4 md:mb-12 mb-8 md:mt-34 mt-24 max-w-[980px] md:flex-row md:items-center'
            }
          >
            <div
              className={
                'flex flex-col justify-center md:space-y-4 space-y-3 text-center md:text-left order-1 md:order-2'
              }
            >
              <div
                className={'text-subTitle md:text-[22px] text-lg font-sf-pro-light underline'}
              >
                {fragrance.brand?.name || 'Unknown Brand'}
              </div>
              <div className={'md:text-5xl text-3xl'}>{fragrance.name}</div>
              <div
                className={
                  'flex items-center justify-center md:justify-start gap-3'
                }
              >
                <StarRatingInput
                  rating={Math.round(averageRating)}
                  size={24}
                  onRatingClicked={handleRatingClicked}
                  className="cursor-pointer"
                />
                <span className={'md:text-lg text-base text-gray-600'}>
                  {reviewCount > 0 ? `(${reviewCount})` : 'No reviews yet'}
                </span>
              </div>
            </div>

            {fragrance.imgUrl && (
              <div className={'flex-shrink-0 order-2 md:order-1'}>
                <img
                  className={
                    'w-[240px] h-[240px] object-contain md:w-[400px] md:h-[400px]'
                  }
                  src={fragrance.imgUrl}
                  alt={fragrance.name}
                />
              </div>
            )}
          </div>
        </div>

        <section className={'max-w-[980px] w-full mx-auto md:px-4 px-3 md:py-8 py-6'}>
          {fragrance.description && (
            <Fieldset className={'md:space-y-8 space-y-6 bg-white md:p-10 p-6 md:mt-25 mt-10 md:mb-15 mb-6'}>
              <Legend className={'md:text-3xl text-2xl md:pb-8 pb-6 border-b-1 border-formSep text-title'}>
                Description
              </Legend>
              {fragrance.description.length < 300 ? (
                <div className={'md:text-xl text-lg text-primaryText whitespace-pre-line'}>{fragrance.description}</div>
              ) : (
                <ReadMoreText
                  lettersLimit={300}
                  className={'md:text-xl text-lg text-primaryText'}
                  textClassName={isDescriptionExpanded ? 'whitespace-pre-line' : ''}
                  expandLabel={'more'}
                  collapseLabel={'less'}
                  buttonStyle={{ color: '#0084ff', textDecoration: undefined }}
                  onExpandChange={setIsDescriptionExpanded}
                >
                  {fragrance.description}
                </ReadMoreText>
              )}
            </Fieldset>
          )}

          <Fieldset className={'md:space-y-[28px] space-y-5 bg-white md:p-10 p-6 md:mb-15 mb-6'}>
            <Legend className={'md:text-3xl text-2xl md:pb-8 pb-6 border-b-1 border-formSep text-title'}>
              Details
            </Legend>
            <DetailRow
              label={'Country'}
              value={fragrance.country?.name || 'Unknown'}
            />
            <DetailRow
              label={'Year'}
              value={fragrance.year?.toString() || 'Unknown'}
            />
            <DetailRow label={'Price'} value={priceLabel} />
            <DetailRow
              label={'Perfumer'}
              value={fragrance.perfumers.length > 0 ? fragrance.perfumers.map(p => p.name).join(', ') : 'Unknown'}
              isLast
            />
          </Fieldset>

          <Fieldset className={'md:space-y-[28px] space-y-5 bg-white md:p-10 p-6 md:mb-15 mb-6'}>
            <Legend className={'md:text-3xl text-2xl md:pb-8 pb-6 border-b-1 border-formSep text-title'}>
              Ingredients
            </Legend>
            {fragrance.topNotes.length > 0 && (
              <DetailRowWithPills
                label={'Top notes'}
                notes={fragrance.topNotes}
              />
            )}
            {fragrance.middleNotes.length > 0 && (
              <DetailRowWithPills
                label={'Middle notes'}
                notes={fragrance.middleNotes}
              />
            )}
            {fragrance.bottomNotes.length > 0 && (
              <DetailRowWithPills
                label={'Base notes'}
                notes={fragrance.bottomNotes}
                isLast
              />
            )}
          </Fieldset>

          {/* Reviews Section */}
          {reviewId ? (
            <SingleReviewSection reviewId={reviewId} highlightReplyId={replyId} />
          ) : (
            <ReviewsSection fragranceId={fragrance.id} />
          )}
        </section>
      </div>
      <WriteReviewDialog
        isOpen={isReviewDialogOpen}
        onClose={() => setIsReviewDialogOpen(false)}
        fragranceId={fragrance.id}
        initialRating={initialReviewRating}
      />
    </>
  )
}
